import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useOutletContext } from "react-router-dom";
import "./ReviewMappingsModal.scss";
import {
  MotifSelect,
  MotifOption,
  MotifFormField,
} from "@ey-xd/motif-react";
import { ExcelIcon } from "../../assets/Icons/ExcelIcon";
import { ArrowLeftIcon } from "../../assets/Icons/ArrowLeft";
import { RefreshIcon } from "../../assets/Icons/Refresh";
import { AILogo } from "../../assets/Icons/SparksSolid";
import { getColumnsForFileType, isMandatory } from "./reviewMappingsConfig";
import { SAMPLE_CLIENT_HEADERS, SAMPLE_VALUES, getSampleValue, FORMULA_FUNCTIONS, evaluateFormula, validateTextFunctionQuotes } from "./reviewMappingsDummyData";
import { InfoIcon } from "../../assets/Icons/InfoIcon";
import { EyeIcon } from "../../assets/Icons/EyeIcon";
import { fetchPreviewSheets } from "./PreviewFileData";
import { InfoCircleIcon } from "../../assets/Icons/InfoCircleIcon";
import WarningTriangle from "../../assets/Icons/WarningTriangle";
import { CloseIcon } from "../../assets/Icons/CloseIcon";
import { AIAnomaliesModal } from "./AIAnomaliesModal";
import { AIService } from "../../api/services/ai.service";
import { FilesService } from "../../api/services/files.service";
import { createExcelFile } from "../../util/excelUtil";
import { CustomTooltipTop } from "../CommonUIComponents/CustomTooltip/CustomTooltipTop";
import { CHAT_ACTIONS } from "../../util/constants";
import { CustomTooltipBottom } from "../CommonUIComponents/CustomTooltip/CustomTooltipBottom";

// Survives re-mounts so previews are fetched once per file, not on every visit
const sheetPreviewCache = new Map();

const normalizeSheetKey = (name) => String(name || "").replace(/\s+/g, "").toLowerCase();

const getColumnLabel = (index) => {
  let label = "";
  let value = index;
  do {
    label = String.fromCharCode(65 + (value % 26)) + label;
    value = Math.floor(value / 26) - 1;
  } while (value >= 0);
  return label;
};

// Modal for reviewing and editing field mappings for uploaded files
export const ReviewMappingsModal = ({ show = true, onClose, onConfirm, onIncompleteData, uploadedFiles = [] }) => {
  // Get user from Redux store
  const user = useSelector((state) => state.user?.user);
  const userEmail = user?.email || user?.mail || user?.username || "";

  // Get currentOrder from Redux to extract the axonId
  const currentOrder = useSelector((state) => state.orders?.currentOrder);
  const axonId = currentOrder?.[0]?.headerSection?.[0]?.axonId;
  // Numeric order id (not the route GUID)
  const orderId = currentOrder?.[0]?.headerSection?.[0]?.requestId;

  const [activeTab, setActiveTab] = useState("CY");
  const [fileType, setFileType] = useState("Journal Entry (JE)");
  const [showAIAnomaliesModal, setShowAIAnomaliesModal] = useState(false);
  const [showComputation, setShowComputation] = useState(false);
  const [showSampleOutput, setShowSampleOutput] = useState(false);
  // Track single selected/expanded file (source for apply operation)
  const [selectedFileId, setSelectedFileId] = useState(null);
  // Track target files for "Apply to multiple files" operation
  const [targetFileIds, setTargetFileIds] = useState([]);
  // Track selected column headers for "Apply to multiple files"
  const [selectedColumns, setSelectedColumns] = useState([]);
  // Track mappings: { fileId: { columnId: [selectedHeaders] } }
  const [mappings, setMappings] = useState({});
  // Track formulas: { fileId: { columnId: formulaString } }
  const [formulas, setFormulas] = useState({});
  // Track formula validation errors: { fileId: { columnId: errorMessage } }
  const [formulaErrors, setFormulaErrors] = useState({});
  // Track active formula input for suggestions
  const [activeFormulaInput, setActiveFormulaInput] = useState(null); // { fileId, columnId }
  const [formulaSuggestions, setFormulaSuggestions] = useState([]);
  const formulaInputRefs = useRef({});
  // Store file data triage response from API
  const [fileDataTriageResponse, setFileDataTriageResponse] = useState([]);
  // Track saving state
  const [isSaving, setIsSaving] = useState(false);
  // Track confirming state
  const [isConfirming, setIsConfirming] = useState(false);
  // Track refreshing state
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Track initial loading state (when modal opens)
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  // Toast message for apply to multiple files exceptions
  const [applyToastMessage, setApplyToastMessage] = useState(null);
  const [usePyClosingBalance, setUsePyClosingBalance] = useState(false);
  // Sheet preview data keyed by order|file|type|period: { status, sheets }
  const [sheetPreviews, setSheetPreviews] = useState({});
  const [previewPopup, setPreviewPopup] = useState(null);
  // Create a Set of uploaded file identifiers (filename + period) for efficient lookup
  // Excludes files with "Replaced" or "Deleted" status
  const uploadedFileIdentifiers = useMemo(() => {
    const fileIdentifiers = new Set();
    if (uploadedFiles && Array.isArray(uploadedFiles)) {
      uploadedFiles.forEach(section => {
        if (section.files && Array.isArray(section.files)) {
          section.files.forEach(file => {
            // Skip files with "Replaced" or "Deleted" status
            const fileStatus = (file.status || "").toLowerCase();
            if (fileStatus === "replaced" || fileStatus === "deleted") {
              return;
            }
            const fileName = file.name || file.fileName;
            const period = file.period || "";
            if (fileName) {
              // Use filename + period combination for unique identification
              const identifier = `${fileName.trim()}|${period.toUpperCase()}`;
              fileIdentifiers.add(identifier);
            }
          });
        }
      });
    }
    return fileIdentifiers;
  }, [uploadedFiles]);
  
const formatDataTriageStatus = (status) => {
  if (!status) return "";
  const normalizedStatus = String(status).trim();
  const displayStatuses = {
    AIHeadersMapped: "AI MAPPED",
    DraftSavedPostAIMapping: "DRAFT SAVED",
    ExtractingHeaders: "EXTRACTING...",
    Uploading: "UPLOADING...",
  };
  return displayStatuses[normalizedStatus] ||
    normalizedStatus.replace(/([a-z])([A-Z])/g, "$1 $2").toUpperCase();
};

  // Parse a CSV string of headers into a trimmed array
  const parseCsvHeaders = useCallback((csvString) => {
    if (!csvString || typeof csvString !== 'string') return [];
    return csvString.split(',').map(header => header.trim()).filter(header => header);
  }, []);

  // Statuses that indicate a file is actionable (can be mapped/confirmed)
  const ACTIONABLE_STATUSES = ["AIHeadersMapped", "DraftSavedPostAIMapping"];
  const isActionableStatus = useCallback((status) => ACTIONABLE_STATUSES.includes(status), []);

  // Map selected fileType dropdown label to API file_type codes
  const getFileTypeCode = useCallback((fileTypeLabel) => {
    if (!fileTypeLabel) return "";
    // Extract abbreviation inside parentheses e.g. "Journal Entry (JE)" -> "JE"
    const match = fileTypeLabel.match(/\(([^)]+)\)/);
    return match ? match[1].trim().toUpperCase() : fileTypeLabel.trim().toUpperCase();
  }, []);

  // Map a raw period value from API to the normalized tab code ("CY" | "PY").
  // Accepts variations like "CY", "Current Year", "current_year", "PY", "Previous Year", etc.
  const getPeriodCode = useCallback((periodValue) => {
    if (periodValue === null || periodValue === undefined) return "";
    const raw = periodValue.toString().trim().toUpperCase();
    if (!raw) return "";
    if (raw === "CY" || raw.includes("CURRENT")) return "CY";
    if (raw === "PY" || raw.includes("PREVIOUS") || raw.includes("PRIOR")) return "PY";
    return raw;
  }, []);

  // Check whether a file's API fileType AND period match the current tab + dropdown selection
  const matchesSelectedFileType = useCallback((fileData) => {
    if (!fileData) return false;
    const apiFileType = (fileData.file_type || fileData.fileType || "").toString().trim().toUpperCase();
    if (!apiFileType) return false;
    const selectedCode = getFileTypeCode(fileType);
    const typeMatches = apiFileType === selectedCode || apiFileType === fileType?.toUpperCase();
    if (!typeMatches) return false;

    // Also match the active tab's period (CY / PY) against the file's period field.
    const filePeriod = getPeriodCode(fileData.period);
    // If the API has no period information, fall back to treating it as the current tab.
    if (!filePeriod) return true;
    return filePeriod === activeTab;
  }, [fileType, getFileTypeCode, getPeriodCode, activeTab]);

  const getSheetPreviewKey = useCallback((file) => [
    orderId,
    (file?.file_name || "").trim(),
    (file?.file_type || file?.fileType || "JE").toString().trim().toUpperCase(),
    getPeriodCode(file?.period) || "CY",
  ].join("|"), [orderId, getPeriodCode]);

  const getFileDataById = useCallback((fileId) =>
    fileDataTriageResponse.find(
      (file, index) => (file.file_data_triage_id || index + 1) === fileId,
    ),
  [fileDataTriageResponse]);

  // Get dropdown options (raw headers) for a specific file/sheet record.
  const getClientHeadersForFile = useCallback((fileId) => {
    const fileData = getFileDataById(fileId);
    if (fileData && matchesSelectedFileType(fileData) && fileData.header_json) {
      const parsedHeaders = parseCsvHeaders(fileData.header_json);
      if (parsedHeaders.length > 0) {
        return parsedHeaders;
      }
    }
    // Fallback to dummy client headers based on file type
    return SAMPLE_CLIENT_HEADERS[fileType] || [];
  }, [getFileDataById, parseCsvHeaders, fileType, matchesSelectedFileType]);

  // Parse first_row JSON string and get sample values for a specific file
  const getFirstRowDataForFile = useCallback((fileId) => {
    const fileData = getFileDataById(fileId);
    if (fileData && matchesSelectedFileType(fileData) && fileData.first_row) {
      try {
        return JSON.parse(fileData.first_row);
      } catch (e) {
        console.error("Error parsing first_row JSON:", e);
        return {};
      }
    }
    return {};
  }, [getFileDataById, matchesSelectedFileType]);

  // Get sample value for a header from the first_row data of a specific file
  const getSampleValueForFile = useCallback((fileId, headerName) => {
    const firstRowData = getFirstRowDataForFile(fileId);
    if (firstRowData && headerName in firstRowData) {
      const value = firstRowData[headerName];
      // Return the value, or "--" if empty
      return value !== "" && value !== null && value !== undefined ? value : "--";
    }
    // Fallback to dummy sample values
    return getSampleValue(headerName);
  }, [getFirstRowDataForFile]);

  // File type options
  const fileTypeOptions = [
    "Journal Entry (JE)",
    "Trial Balance (TB)",
    "Chart of Accounts (COA)",
  ];

  // Get columns based on selected file type.
  // Prefer API's client_headers CSV (from any file matching the selected fileType) as the
  // scrollable column headers; fallback to static config if API data is not yet available.
  // Each column's mandatory flag is derived from ai_mapped_headers[].is_mandatory
  // (matched by standard_header for files of the currently selected fileType).
  const tableColumns = useMemo(() => {
    const matchingFiles = fileDataTriageResponse.filter(f => matchesSelectedFileType(f));
    const matchingFile = matchingFiles.find(f => f?.client_headers);

    // Build a lookup of standard_header -> is_mandatory from ai_mapped_headers across
    // all files of the selected fileType. If any file marks a header mandatory, treat it
    // as mandatory.
    const norm = (s) =>
      (s || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, " ");
    const mandatoryLookup = {};
    matchingFiles.forEach(file => {
      let mappedArray = file.ai_mapped_headers;
      if (typeof mappedArray === 'string') {
        try {
          mappedArray = JSON.parse(mappedArray);
        } catch (e) {
          mappedArray = null;
        }
      }
      if (!Array.isArray(mappedArray)) return;
      mappedArray.forEach(item => {
        if (!item || !item.standard_header) return;
        const key = norm(item.standard_header);
        if (mandatoryLookup[key] !== true) {
          mandatoryLookup[key] = item.is_mandatory === true || item.is_mandatory === "true";
        }
      });
    });

    if (matchingFile?.client_headers) {
      const parsed = parseCsvHeaders(matchingFile.client_headers);
      if (parsed.length > 0) {
        return parsed.map(header => {
          const isMand = mandatoryLookup[norm(header)] === true;
          return {
            id: header,
            name: header,
            type: isMand ? "mandatory" : "recommended",
          };
        });
      }
    }
    return getColumnsForFileType(fileType);
  }, [fileType, fileDataTriageResponse, matchesSelectedFileType, parseCsvHeaders]);

  // Get client headers based on selected file type
  const clientHeaders = useMemo(() => {
    return SAMPLE_CLIENT_HEADERS[fileType] || [];
  }, [fileType]);

  // Handle mapping selection for a file and column
  const handleMappingChange = useCallback((fileId, columnId, selectedValues) => {
    // Direct state update - removed setTimeout as it interferes with
    // MotifSelect's filter functionality, causing filtered options to not be selectable
    setMappings(prev => ({
      ...prev,
      [fileId]: {
        ...prev[fileId],
        [columnId]: selectedValues,
      }
    }));
  }, []);

  // Get current mapping for a file and column
  const getMapping = useCallback((fileId, columnId) => {
    return mappings[fileId]?.[columnId] || [];
  }, [mappings]);

  // Evaluate formula with sample values - handles various formula patterns
  const evaluateFormulaWithSampleValues = useCallback((formula, fileId) => {
    if (!formula || formula.trim() === "" || formula === "---") {
      return null;
    }

    // Get the first_row data for this file
    const firstRowData = getFirstRowDataForFile(fileId);

    try {
      // Check if formula starts with = (standard formula)
      if (formula.startsWith("=")) {
        // Pass first_row data to evaluateFormula so it can use actual values
        const fileData = getFileDataById(fileId);
        return evaluateFormula(formula, firstRowData, {
          fileName: fileData?.file_name,
          sheetName: fileData?.sheet_name || fileData?.sheetName || fileData?.sheet,
        });
      }

      // First, replace all header references [Header Name] with their sample values
      const headerPattern = /\[([^\]]+)\]/g;
      let processedFormula = formula;
      let match;
      let hasHeaders = false;
      const replacements = [];

      // Collect all header references first
      while ((match = headerPattern.exec(formula)) !== null) {
        hasHeaders = true;
        const header = match[1];
        // Get sample value from first_row data or fallback to SAMPLE_VALUES
        const sampleValue = (firstRowData && header in firstRowData && firstRowData[header] !== "") 
          ? firstRowData[header] 
          : (SAMPLE_VALUES[header] || `{${header}}`);
        replacements.push({ original: `[${header}]`, replacement: sampleValue });
      }

      // Apply all replacements
      replacements.forEach(({ original, replacement }) => {
        processedFormula = processedFormula.split(original).join(replacement);
      });

      // Handle function patterns like CONCAT(), TRIM(), DATE_FORMAT(), etc.
      const funcPattern = /^([A-Z_]+)\((.*)\)$/i;
      const funcMatch = processedFormula.match(funcPattern);
      
      if (funcMatch) {
        const funcName = funcMatch[1].toUpperCase();
        const args = funcMatch[2];

        // Return a sample output based on function type
        switch (funcName) {
          case "CONCAT":
            // Split by comma but preserve quoted strings
            const concatParts = args.split(/,(?=(?:[^']*'[^']*')*[^']*$)/).map(s => s.trim().replace(/^'|'$/g, ""));
            return concatParts.join("");
          case "TRIM":
            return args.trim();
          case "UPPER":
            return args.toUpperCase();
          case "LOWER":
            return args.toLowerCase();
          case "DATE_FORMAT":
            // Just return a formatted date sample
            return "2024-03-15";
          case "LEFT":
            const leftParts = args.split(",");
            const leftNum = parseInt(leftParts[1]?.trim()) || 5;
            return leftParts[0]?.trim().slice(0, leftNum) || "";
          case "RIGHT":
            const rightParts = args.split(",");
            const rightNum = parseInt(rightParts[1]?.trim()) || 5;
            return rightParts[0]?.trim().slice(-rightNum) || "";
          default:
            return `${funcName}(${args})`;
        }
      }

      // If we replaced headers but no function, return the processed result
      if (hasHeaders) {
        return processedFormula;
      }

      return null;
    } catch (e) {
      console.error("Formula evaluation error:", e);
      return null;
    }
  }, [getFirstRowDataForFile, getFileDataById]);

  // Get sample output for a mapping or formula
  const getSampleOutputForMapping = useCallback((fileId, columnId) => {
    const selectedHeaders = getMapping(fileId, columnId);
    const formula = formulas[fileId]?.[columnId];
    const hasFormula = formula && formula.trim() !== "";
    
    // If there's a formula, try to evaluate it
    if (hasFormula) {
      const formulaResult = evaluateFormulaWithSampleValues(formula, fileId);
      if (formulaResult !== null) {
        return formulaResult;
      }
    }

    // If no headers selected and no formula → blank
    if (selectedHeaders.length === 0 && !hasFormula) {
      return "";
    }

    // If multiple headers selected and no formula → blank (user needs to apply formula)
    if (selectedHeaders.length > 1 && !hasFormula) {
      return "";
    }

    // Single header selected → show sample value for that header
    if (selectedHeaders.length === 1) {
      return getSampleValueForFile(fileId, selectedHeaders[0]);
    }

    return "";
  }, [formulas, getMapping, getSampleValueForFile, evaluateFormulaWithSampleValues]);

  // Validate formula for bracket mismatches
  const validateFormulaBrackets = useCallback((formula) => {
    if (!formula || formula.trim() === "") {
      return null; // Empty formula is valid (no error)
    }

    let openBrackets = 0;
    let closeBrackets = 0;
    let hasUnmatchedOpen = false;
    let hasUnmatchedClose = false;

    for (let i = 0; i < formula.length; i++) {
      if (formula[i] === "[") {
        openBrackets++;
      } else if (formula[i] === "]") {
        closeBrackets++;
        if (closeBrackets > openBrackets) {
          hasUnmatchedClose = true;
        }
      }
    }

    hasUnmatchedOpen = openBrackets > closeBrackets;

    // Check for incomplete bracket patterns
    // Pattern like "[field" without closing bracket
    const incompleteOpenPattern = /\[[^\]]*$/;
    // Pattern like "field]" without opening bracket
    const incompleteClosePattern = /^[^\[]*\]|[^\[]\][^\[\]]*$/;

    if (openBrackets !== closeBrackets) {
      if (hasUnmatchedOpen) {
        return "Invalid formula: Missing closing bracket ']'";
      }
      if (hasUnmatchedClose) {
        return "Invalid formula: Missing opening bracket '['";
      }
    }

    // Check for empty brackets []
    if (formula.includes("[]")) {
      return "Invalid formula: Empty field reference '[]'";
    }

    // Check for nested brackets which are not allowed [[field]]
    if (/\[\[|\]\]/.test(formula)) {
      return "Invalid formula: Nested brackets are not allowed";
    }

    return null; // No error
  }, []);

  // Get formula error for a file and column
  const getFormulaError = useCallback((fileId, columnId) => {
    const storedError = formulaErrors[fileId]?.[columnId];
    if (storedError) return storedError;
    const formula = formulas[fileId]?.[columnId];
    if (!formula || formula.trim() === "") return null;
    const output = getSampleOutputForMapping(fileId, columnId);
    if (output === null || output === undefined || String(output).trim() === "") {
      return "No output for given formula";
    }
    return null;
  }, [formulaErrors, formulas, getSampleOutputForMapping]);

  // Handle formula input change
  const generateFormulaSuggestionsRef = useRef(() => {});
  const handleFormulaChange = useCallback((fileId, columnId, value) => {
    setFormulas(prev => ({
      ...prev,
      [fileId]: {
        ...prev[fileId],
        [columnId]: value,
      }
    }));

    // Validate formula brackets and TEXT() quoting
    const error = validateFormulaBrackets(value) || validateTextFunctionQuotes(value);
    setFormulaErrors(prev => ({
      ...prev,
      [fileId]: {
        ...prev[fileId],
        [columnId]: error,
      }
    }));
    
    // Generate suggestions based on input - debounced
    generateFormulaSuggestionsRef.current(value, fileId, columnId);
  }, [validateFormulaBrackets]);

  // Get current formula for a file and column
  const getFormula = useCallback((fileId, columnId) => {
    return formulas[fileId]?.[columnId] || "";
  }, [formulas]);

  // Clear formula for a file and column
  const clearFormula = useCallback((fileId, columnId) => {
    setFormulas(prev => ({
      ...prev,
      [fileId]: {
        ...prev[fileId],
        [columnId]: "",
      }
    }));
    // Also clear any formula error
    setFormulaErrors(prev => ({
      ...prev,
      [fileId]: {
        ...prev[fileId],
        [columnId]: null,
      }
    }));
    setFormulaSuggestions([]);
    setActiveFormulaInput(null);
  }, []);

  // Generate suggestions based on input text
  const generateFormulaSuggestions = useCallback((inputText, fileId, columnId) => {
    if (!inputText || inputText.trim() === "") {
      setFormulaSuggestions([]);
      setActiveFormulaInput(null);
      return;
    }

    setActiveFormulaInput({ fileId, columnId });

    // Get the last word being typed (after = or ( or , or space)
    const lastWordMatch = inputText.match(/[=\(\,\s]?([A-Za-z_][A-Za-z0-9_]*)$/);
    // If the last char is a trigger (=, (, ,, space) without any letters yet,
    // show all suggestions instead of filtering by the whole input.
    const trailingTrigger = /[=\(\,\s]$/.test(inputText);
    const searchTerm = lastWordMatch
      ? lastWordMatch[1].toLowerCase()
      : trailingTrigger
        ? ""
        : inputText.toLowerCase();

    const suggestions = [];

    // Suggest only the client headers selected in the dropdown for THIS specific column.
    // So if the user picks two headers for "Effective Date", only those two show up as
    // suggestions in the formula input of the "Effective Date" column.
    const fileMappings = mappings[fileId] || {};
    const columnMappings = Array.isArray(fileMappings[columnId]) ? fileMappings[columnId] : [];

    columnMappings.forEach(header => {
      if (!header) return;
      if (!searchTerm || header.toLowerCase().includes(searchTerm)) {
        suggestions.push({ type: "header", value: header, display: header });
      }
    });

    // Search in formula functions
    FORMULA_FUNCTIONS.forEach(func => {
      if (!searchTerm || func.name.toLowerCase().includes(searchTerm)) {
        suggestions.push({ type: "function", value: func.name, display: func.name, description: func.description });
      }
    });

    setFormulaSuggestions(suggestions.slice(0, 10)); // Limit to 10 suggestions
  }, [mappings]);

  // Keep the ref pointing to the latest suggestion generator so handleFormulaChange
  // (which has stable deps) always calls the fresh function with current mappings.
  useEffect(() => {
    generateFormulaSuggestionsRef.current = generateFormulaSuggestions;
  }, [generateFormulaSuggestions]);

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback((fileId, columnId, suggestion) => {
    setFormulas(prev => {
      const currentFormula = prev[fileId]?.[columnId] || "";
      
      // Replace the last word with the selected suggestion
      const lastWordMatch = currentFormula.match(/[=\(\,\s]?([A-Za-z_][A-Za-z0-9_]*)$/);
      let newFormula;
      
      if (lastWordMatch) {
        const insertValue = suggestion.type === "header" ? `[${suggestion.value}]` : `${suggestion.value}(`;
        newFormula = currentFormula.slice(0, currentFormula.length - lastWordMatch[1].length) + insertValue;
      } else {
        const insertValue = suggestion.type === "header" ? `[${suggestion.value}]` : `${suggestion.value}(`;
        newFormula = currentFormula + insertValue;
      }

      return {
        ...prev,
        [fileId]: {
          ...prev[fileId],
          [columnId]: newFormula,
        }
      };
    });

    setFormulaSuggestions([]);
    setActiveFormulaInput(null);

    // Focus back on the input - use setTimeout to avoid issues
    setTimeout(() => {
      const inputKey = `${fileId}-${columnId}`;
      if (formulaInputRefs.current[inputKey]) {
        formulaInputRefs.current[inputKey].focus();
      }
    }, 0);
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !e.target.closest('.formula-input-wrapper') &&
        !e.target.closest('.formula-suggestions-dropdown--portal')
      ) {
        setFormulaSuggestions([]);
        setActiveFormulaInput(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset source file selection when file type or period (CY/PY) changes
  // This prevents carrying forward source file for "apply to multiple files" logic
  useEffect(() => {
    setSelectedFileId(null);
    setTargetFileIds([]);
    setSelectedColumns([]);
  }, [activeTab, fileType]);

  // Fetch file data triage when modal opens
  useEffect(() => {
    if (show && axonId) {
      const fetchFileDataTriage = async () => {
        setIsInitialLoading(true);
        try {
          const response = await AIService.getFileDataTriage(axonId);
          console.log("File Data Triage API Response:", response);
          // Store the response data
          if (Array.isArray(response)) {
            setFileDataTriageResponse(response);
          } else if (response?.data && Array.isArray(response.data)) {
            setFileDataTriageResponse(response.data);
          }
        } catch (error) {
          console.error("Error fetching file data triage:", error);
        } finally {
          setIsInitialLoading(false);
        }
      };
      fetchFileDataTriage();
    }
  }, [show, axonId]);

  // Pre-populate mappings from computation_formula (if available) or ai_mapped_headers in the API response.
  // Priority: computation_formula (user-saved mappings) > ai_mapped_headers (AI-generated mappings)
  // For each file, iterate the array of { standard_header, raw_header, computation_formula }
  // and pre-select raw_header values in the dropdown for the matching table column (standard_header).
  useEffect(() => {
    if (!fileDataTriageResponse || fileDataTriageResponse.length === 0) return;

    const initialMappings = {};
    const initialFormulas = {};

    // Helper: normalize a string for loose matching (trim + lowercase + collapse spaces/underscores)
    const norm = (s) =>
      (s || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, " ");

    fileDataTriageResponse.forEach((file, index) => {
      const fileId = file.file_data_triage_id || index + 1;

      // Build a lookup of this file's standard headers (client_headers CSV) so we can
      // resolve standard_header to the exact column id used in the UI.
      const fileClientHeaders = parseCsvHeaders(file.client_headers);
      const clientHeaderLookup = {};
      fileClientHeaders.forEach(h => {
        clientHeaderLookup[norm(h)] = h;
      });

      // Check if computation_formula exists (user-saved mappings from save/confirm)
      // If it does, use it; otherwise fall back to ai_mapped_headers
      let sourceArray = null;
      let isFromComputationFormula = false;

      // Try to parse computation_formula first (priority)
      if (file.computation_formula) {
        let computationFormula = file.computation_formula;
        if (typeof computationFormula === 'string') {
          try {
            computationFormula = JSON.parse(computationFormula);
          } catch (e) {
            computationFormula = null;
          }
        }
        if (Array.isArray(computationFormula) && computationFormula.length > 0) {
          sourceArray = computationFormula;
          isFromComputationFormula = true;
        }
      }

      // Fall back to ai_mapped_headers if computation_formula is not available
      if (!sourceArray) {
        let mappedArray = file.ai_mapped_headers;
        if (typeof mappedArray === 'string') {
          try {
            mappedArray = JSON.parse(mappedArray);
          } catch (e) {
            mappedArray = null;
          }
        }
        if (Array.isArray(mappedArray) && mappedArray.length > 0) {
          sourceArray = mappedArray;
        }
      }

      if (!sourceArray || sourceArray.length === 0) return;

      sourceArray.forEach(item => {
        if (!item || !item.standard_header) return;

        // Resolve columnId to the exact value used as tableColumns[].id.
        // tableColumns.id is the client_header string (e.g. "JE Number").
        const resolvedColumnId =
          clientHeaderLookup[norm(item.standard_header)] || item.standard_header;

        // Normalize raw_header to an array of strings
        let rawHeaders = item.raw_header;
        if (typeof rawHeaders === 'string') {
          const trimmed = rawHeaders.trim();
          if (trimmed.startsWith('[')) {
            try {
              rawHeaders = JSON.parse(trimmed);
            } catch (e) {
              rawHeaders = trimmed.split(',').map(s => s.trim()).filter(Boolean);
            }
          } else {
            rawHeaders = trimmed
              ? trimmed.split(',').map(s => s.trim()).filter(Boolean)
              : [];
          }
        } else if (!Array.isArray(rawHeaders)) {
          rawHeaders = rawHeaders ? [rawHeaders] : [];
        }

        // Ensure all entries are strings
        rawHeaders = rawHeaders
          .map(h => (h == null ? "" : h.toString().trim()))
          .filter(Boolean);

        if (rawHeaders.length > 0) {
          if (!initialMappings[fileId]) initialMappings[fileId] = {};
          initialMappings[fileId][resolvedColumnId] = rawHeaders;
        }

        // Handle computation_formula field
        if (item.computation_formula && item.computation_formula.toString().trim() !== "") {
          if (!initialFormulas[fileId]) initialFormulas[fileId] = {};
          initialFormulas[fileId][resolvedColumnId] = item.computation_formula;
        }
      });
    });

    // Overwrite mappings/formulas with API defaults when response arrives.
    // (If you want user edits to persist across refreshes, merge instead.)
    setMappings(initialMappings);
    setFormulas(initialFormulas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileDataTriageResponse]);

  // Generate filesData from API response - filtered by selected fileType and uploaded files
  // IMPORTANT: We need to preserve the original array index for consistent fileId calculation
  const filesData = useMemo(() => {
    if (!fileDataTriageResponse || fileDataTriageResponse.length === 0) {
      return [];
    }
    return fileDataTriageResponse
      .map((file, originalIndex) => ({
        file,
        originalIndex,
        // Use consistent fileId calculation: file_data_triage_id or original array index + 1
        id: file.file_data_triage_id || originalIndex + 1,
      }))
      .filter(({ file }) => {
        // First check if file matches the selected file type and period
        if (!matchesSelectedFileType(file)) return false;
        
        // Then check if file exists in the uploaded files list (from OrderDetailsFileUpload)
        // If uploadedFileIdentifiers is empty, show all files (backwards compatibility)
        if (uploadedFileIdentifiers.size === 0) return true;
        
        // Use filename + period combination for matching
        const fileName = (file.file_name || "").trim();
        const filePeriod = getPeriodCode(file.period);
        const identifier = `${fileName}|${filePeriod}`;
        return uploadedFileIdentifiers.has(identifier);
      })
      .map(({ file, id }) => ({
        id,
        fileName: file.file_name,
        previewKey: getSheetPreviewKey(file),
        sheetName: file.sheet_name || file.sheetName || file.sheet || "",
        status: file.data_triage_status,
        disabled: !isActionableStatus(file.data_triage_status),
      }));
  }, [fileDataTriageResponse, matchesSelectedFileType, uploadedFileIdentifiers, getPeriodCode, isActionableStatus, getSheetPreviewKey]);

  // Prefetch previews for every file up front so the eye icon opens instantly
  useEffect(() => {
    if (!orderId || !Array.isArray(fileDataTriageResponse)) return undefined;
    let isActive = true;
    const requests = new Map();
    fileDataTriageResponse.forEach((file) => {
      const sourceFileName = (file?.file_name || "").trim();
      if (!sourceFileName) return;
      const key = getSheetPreviewKey(file);
      if (requests.has(key)) return;
      requests.set(key, {
        actualOrderId: orderId,
        sourceFileName,
        fileType: (file.file_type || file.fileType || "JE").toString().trim().toUpperCase(),
        financialYear: getPeriodCode(file.period) || "CY",
      });
    });

    requests.forEach((params, key) => {
      if (!sheetPreviewCache.has(key)) {
        const request = fetchPreviewSheets(params).catch((error) => {
          sheetPreviewCache.delete(key);
          throw error;
        });
        sheetPreviewCache.set(key, request);
      }
      setSheetPreviews((current) =>
        current[key] ? current : { ...current, [key]: { status: "loading", sheets: [] } },
      );
      sheetPreviewCache.get(key).then(
        (sheets) => {
          if (isActive) setSheetPreviews((current) => ({ ...current, [key]: { status: "ready", sheets } }));
        },
        (error) => {
          console.error("Failed to prefetch sheet preview:", error);
          if (isActive) setSheetPreviews((current) => ({ ...current, [key]: { status: "error", sheets: [] } }));
        },
      );
    });

    return () => {
      isActive = false;
    };
  }, [orderId, fileDataTriageResponse, getPeriodCode, getSheetPreviewKey]);

  const previewPopupRows = useMemo(() => {
    if (!previewPopup) return null;
    const entry = sheetPreviews[previewPopup.previewKey];
    if (!entry || entry.status !== "ready") return null;
    const match = entry.sheets.find(([name]) => normalizeSheetKey(name) === normalizeSheetKey(previewPopup.sheetName))
      || (entry.sheets.length === 1 ? entry.sheets[0] : null);
    return match ? match[1] : [];
  }, [previewPopup, sheetPreviews]);

  // Check if at least one file has null status
  const hasAnyNullStatus = useMemo(() => {
    return filesData.some(file => file.status === null);
  }, [filesData]);

  // The second sticky column is offset by the rendered width of the first one
  const mappingsTableRef = useRef(null);
  useEffect(() => {
    const table = mappingsTableRef.current;
    if (!table) return undefined;
    let isActive = true;
    const updateOffset = () => {
      const firstCell = table.querySelector(".file-name-td");
      if (!isActive || !firstCell) return;
      // offsetWidth (not getBoundingClientRect) so the page-body `zoom` doesn't shrink the offset
      table.style.setProperty(
        "--rm-sticky-col-2-left",
        `${firstCell.offsetWidth}px`,
      );
    };
    updateOffset();
    // Web font swap widens the file-name column after the first measurement
    document.fonts?.ready?.then(updateOffset);
    if (typeof ResizeObserver === "undefined") {
      return () => {
        isActive = false;
      };
    }
    // Chrome doesn't reliably report <td> resizes, so watch the table and the cell's inner block
    const observer = new ResizeObserver(updateOffset);
    observer.observe(table);
    table.querySelectorAll(".file-name-cell").forEach((cell) => observer.observe(cell));
    return () => {
      isActive = false;
      observer.disconnect();
    };
  }, [filesData, tableColumns]);

  // Both CY and PY must have an uploaded TB file to offer the closing-balance option
  const hasTbInBothPeriods = useMemo(() => {
    const periodsWithTb = new Set();
    fileDataTriageResponse.forEach(file => {
      const type = (file.file_type || file.fileType || "").toString().trim().toUpperCase();
      if (type !== "TB") return;
      const period = getPeriodCode(file.period);
      const identifier = `${(file.file_name || "").trim()}|${period}`;
      if (uploadedFileIdentifiers.size === 0 || uploadedFileIdentifiers.has(identifier)) {
        periodsWithTb.add(period);
      }
    });
    return periodsWithTb.has("CY") && periodsWithTb.has("PY");
  }, [fileDataTriageResponse, uploadedFileIdentifiers, getPeriodCode]);

  const showPyClosingBalanceOption =
    activeTab === "CY" && getFileTypeCode(fileType) === "TB" && hasTbInBothPeriods;

  // Formulas replaced by the checkbox, kept so unchecking can restore them
  const previousBeginningBalanceFormulasRef = useRef({});

  const handlePyClosingBalanceChange = useCallback((checked) => {
    setUsePyClosingBalance(checked);

    const normalize = (s) => (s || "").toString().trim().toLowerCase().replace(/[\s_]+/g, " ");
    const targets = [];
    fileDataTriageResponse.forEach((file, index) => {
      const type = (file.file_type || file.fileType || "").toString().trim().toUpperCase();
      if (type !== "TB" || getPeriodCode(file.period) !== "CY") return;
      const identifier = `${(file.file_name || "").trim()}|CY`;
      if (uploadedFileIdentifiers.size > 0 && !uploadedFileIdentifiers.has(identifier)) return;
      const columnId = parseCsvHeaders(file.client_headers).find(
        (h) => normalize(h) === "functional beginning balance",
      );
      if (!columnId) return;
      targets.push({ fileId: file.file_data_triage_id || index + 1, columnId });
    });

    if (checked) {
      const previous = {};
      targets.forEach(({ fileId, columnId }) => {
        previous[`${fileId}|${columnId}`] = formulas[fileId]?.[columnId] || "";
      });
      previousBeginningBalanceFormulasRef.current = previous;
    }
    const saved = previousBeginningBalanceFormulasRef.current;

    setFormulas((prev) => {
      const next = { ...prev };
      targets.forEach(({ fileId, columnId }) => {
        next[fileId] = {
          ...next[fileId],
          [columnId]: checked ? "=0" : saved[`${fileId}|${columnId}`] || "",
        };
      });
      return next;
    });

    if (checked) {
      setFormulaErrors((prev) => {
        const next = { ...prev };
        targets.forEach(({ fileId, columnId }) => {
          next[fileId] = { ...next[fileId], [columnId]: null };
        });
        return next;
      });
    }
  }, [fileDataTriageResponse, formulas, getPeriodCode, uploadedFileIdentifiers, parseCsvHeaders]);

  const isFormulaLocked = useCallback((fileId, columnId) => {
    if (!usePyClosingBalance) return false;
    if ((columnId || "").toString().trim().toLowerCase().replace(/[\s_]+/g, " ") !== "functional beginning balance") return false;
    const file = getFileDataById(fileId);
    const type = (file?.file_type || file?.fileType || "").toString().trim().toUpperCase();
    return type === "TB" && getPeriodCode(file?.period) === "CY";
  }, [usePyClosingBalance, getFileDataById, getPeriodCode]);

  // Fetch once per order so later toggles/edits aren't overwritten
  const fetchedPyCyFlagOrderRef = useRef(null);
  useEffect(() => {
    if (!orderId || getFileTypeCode(fileType) !== "TB" || !hasTbInBothPeriods) return;
    if (fetchedPyCyFlagOrderRef.current === orderId) return;
    fetchedPyCyFlagOrderRef.current = orderId;

    FilesService.getPyCyFlag(orderId)
      .then((response) => {
        if (response?.considerPYEndingBalanceForCY === true) {
          handlePyClosingBalanceChange(true);
        } else {
          setUsePyClosingBalance(false);
        }
      })
      .catch((error) => {
        fetchedPyCyFlagOrderRef.current = null;
        console.error("Error fetching PY/CY flag:", error);
      });
  }, [orderId, fileType, hasTbInBothPeriods, getFileTypeCode, handlePyClosingBalanceChange]);

  // Helper to check if a specific file and column has a flag with non-info severity (anomaly)
  const hasAgentSummary = useCallback((fileId, columnId) => {
    const file = fileDataTriageResponse.find(
      f => (f.file_data_triage_id || 0) === fileId || f.file_name === fileId
    );
    if (!file) return false;

    let mappedArray = file.ai_mapped_headers;
    if (typeof mappedArray === 'string') {
      try {
        mappedArray = JSON.parse(mappedArray);
      } catch (e) {
        return false;
      }
    }
    if (!Array.isArray(mappedArray)) return false;

    const norm = (s) =>
      (s || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, " ");

    const normalizedColumnId = norm(columnId);

    return mappedArray.some(item => {
      if (!item || !item.standard_header) return false;
      const normalizedStandardHeader = norm(item.standard_header);
      if (normalizedStandardHeader !== normalizedColumnId) return false;
      
      // Check if any flag has severity other than "info"
      const flags = Array.isArray(item.flags) ? item.flags : [];
      return flags.some(flag => flag && flag.severity && flag.severity.toLowerCase() !== 'info');
    });
  }, [fileDataTriageResponse]);

  // Check if any file has an agent_summary for a specific column
  const hasAnyAgentSummaryForColumn = useCallback((columnId) => {
    return filesData.some(file => hasAgentSummary(file.id, columnId));
  }, [filesData, hasAgentSummary]);

  // Build AI anomalies list from API response for files matching the selected fileType.
  // An anomaly is any flag in ai_mapped_headers whose severity is not "info".
  const aiAnomalies = useMemo(() => {
    if (!fileDataTriageResponse || fileDataTriageResponse.length === 0) return [];

    const anomalies = [];
    fileDataTriageResponse
      .filter(file => matchesSelectedFileType(file))
      .forEach((file, fileIndex) => {
        let mappedArray = file.ai_mapped_headers;
        if (typeof mappedArray === 'string') {
          try {
            mappedArray = JSON.parse(mappedArray);
          } catch (e) {
            mappedArray = null;
          }
        }
        if (!Array.isArray(mappedArray)) return;

        mappedArray.forEach((item, itemIndex) => {
          if (!item) return;
          
          // Check flags array for anomalies (severity other than "info")
          const flags = Array.isArray(item.flags) ? item.flags : [];
          flags.forEach((flag, flagIndex) => {
            if (!flag || !flag.severity || flag.severity.toLowerCase() === 'info') {
              return;
            }

            // Normalize raw_header to an array then join for display
            let rawHeaders = item.raw_header;
            if (typeof rawHeaders === 'string') {
              const trimmed = rawHeaders.trim();
              if (trimmed.startsWith('[')) {
                try {
                  rawHeaders = JSON.parse(trimmed);
                } catch (e) {
                  rawHeaders = trimmed.split(',').map(s => s.trim()).filter(Boolean);
                }
              } else {
                rawHeaders = trimmed
                  ? trimmed.split(',').map(s => s.trim()).filter(Boolean)
                  : [];
              }
            } else if (!Array.isArray(rawHeaders)) {
              rawHeaders = rawHeaders ? [rawHeaders] : [];
            }
            const rawHeadersDisplay = rawHeaders
              .map(h => (h == null ? '' : h.toString().trim()))
              .filter(Boolean)
              .join(', ');

            anomalies.push({
              id: `${file.file_data_triage_id || fileIndex}-${itemIndex}-${flagIndex}`,
              fieldName: item.standard_header || '',
              fileName: file.file_name || '',
              sheetName: file.sheet_name || file.sheetName || file.sheet || '',
              description: flag.message || '',
              severity: flag.severity || '',
              type: flag.type || '',
              aiSuggestedValue: rawHeadersDisplay || '--',
              currentValue: '',
            });
          });
        });
      });

    return anomalies;
  }, [fileDataTriageResponse, matchesSelectedFileType]);

  // Export files data to Excel - only for files from OrderFileUpload page (excludes Replaced/Deleted)
  // Uses the current mappings and formulas state (what user has edited)
  const handleExportToExcel = useCallback(async () => {
    if (!fileDataTriageResponse || fileDataTriageResponse.length === 0) {
      console.warn("No fileDataTriageResponse to export");
      return;
    }

    // Helper: normalize a string for loose matching
    const norm = (s) =>
      (s || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, " ");

    // Helper to check if file is from OrderFileUpload page (excludes Replaced/Deleted)
    const isUploadedFile = (file) => {
      if (uploadedFileIdentifiers.size === 0) return true; // Backwards compatibility
      // Use filename + period combination for matching
      const fileName = (file.file_name || "").trim();
      const filePeriod = getPeriodCode(file.period);
      const identifier = `${fileName}|${filePeriod}`;
      return uploadedFileIdentifiers.has(identifier);
    };

    // Helper to get columns for a file type (same logic as tableColumns)
    const getColumnsForType = (files) => {
      const matchingFile = files.find(f => f?.client_headers);
      
      // Build mandatory lookup
      const mandatoryLookup = {};
      files.forEach(file => {
        let mappedArray = file.ai_mapped_headers;
        if (typeof mappedArray === 'string') {
          try { mappedArray = JSON.parse(mappedArray); } catch (e) { mappedArray = null; }
        }
        if (!Array.isArray(mappedArray)) return;
        mappedArray.forEach(item => {
          if (!item || !item.standard_header) return;
          const key = norm(item.standard_header);
          if (mandatoryLookup[key] !== true) {
            mandatoryLookup[key] = item.is_mandatory === true || item.is_mandatory === "true";
          }
        });
      });

      if (matchingFile?.client_headers) {
        const parsed = parseCsvHeaders(matchingFile.client_headers);
        if (parsed.length > 0) {
          return parsed.map(header => ({
            id: header,
            name: header,
            isMandatory: mandatoryLookup[norm(header)] === true,
          }));
        }
      }
      return [];
    };

    // Helper to compute sample output for a file and column (same as getSampleOutputForMapping)
    // NOTE: This does NOT use getFirstRowDataForFile because that function filters by
    // matchesSelectedFileType (current UI selection). For export, we need to get first_row
    // data directly from the file object regardless of the UI's current fileType/period selection.
    const computeSampleOutput = (fileId, columnId, fileName, file) => {
      const selectedHeaders = mappings[fileId]?.[columnId] || [];
      const formula = formulas[fileId]?.[columnId] || "";
      const hasFormula = formula && formula.trim() !== "";
      
      // Get first_row data directly from the file object (not using matchesSelectedFileType)
      let firstRowData = {};
      if (file?.first_row) {
        try {
          firstRowData = JSON.parse(file.first_row);
        } catch (e) {
          console.error("Error parsing first_row JSON for export:", e);
          firstRowData = {};
        }
      }

      // If there's a formula, try to evaluate it
      if (hasFormula) {
        if (firstRowData && Object.keys(firstRowData).length > 0) {
          try {
            const { evaluateFormula } = require('./reviewMappingsDummyData');
            const result = evaluateFormula(formula, firstRowData, {
              fileName: file?.file_name,
              sheetName: file?.sheet_name || file?.sheetName || file?.sheet,
            });
            if (result !== null && result !== undefined) {
              return String(result);
            }
          } catch (e) {
            console.error("Formula evaluation error:", e);
          }
        }
      }

      // If no headers selected and no formula → blank
      if (selectedHeaders.length === 0 && !hasFormula) {
        return "";
      }

      // If multiple headers selected and no formula → blank
      if (selectedHeaders.length > 1 && !hasFormula) {
        return "";
      }

      // Single header selected → show sample value
      if (selectedHeaders.length === 1) {
        if (firstRowData && Object.keys(firstRowData).length > 0) {
          const headerKey = selectedHeaders[0];
          // Try exact match first
          if (firstRowData[headerKey] !== undefined) {
            return String(firstRowData[headerKey]);
          }
          // Try case-insensitive match
          const lowerKey = headerKey.toLowerCase();
          const matchingKey = Object.keys(firstRowData).find(k => k.toLowerCase() === lowerKey);
          if (matchingKey) {
            return String(firstRowData[matchingKey]);
          }
        }
      }

      return "";
    };

    // Group files by file type and period - only include uploaded files
    const fileTypes = ["JE", "TB", "COA"];
    const periods = ["CY", "PY"];
    const sheets = [];

    fileTypes.forEach(fileTypeCode => {
      periods.forEach(period => {
        // Filter files for this file type and period - ONLY from uploaded files
        const filesForSheet = fileDataTriageResponse.filter(file => {
          // First check if file is from OrderFileUpload page
          if (!isUploadedFile(file)) return false;

          const apiFileType = (file.file_type || file.fileType || "").toString().trim().toUpperCase();
          const filePeriod = (() => {
            const raw = (file.period || "").toString().trim().toUpperCase();
            if (!raw) return "";
            if (raw === "CY" || raw.includes("CURRENT")) return "CY";
            if (raw === "PY" || raw.includes("PREVIOUS") || raw.includes("PRIOR")) return "PY";
            return raw;
          })();
          
          // Match file type
          const typeMatches = apiFileType === fileTypeCode;
          // Match period (or include if no period specified)
          const periodMatches = !filePeriod || filePeriod === period;
          
          return typeMatches && periodMatches;
        });

        if (filesForSheet.length === 0) return;

        // Get columns for this file type (same as displayed in UI)
        const columns = getColumnsForType(filesForSheet);
        if (columns.length === 0) return;

        const sheetData = [];

        filesForSheet.forEach((file) => {
          // Use consistent fileId calculation
          const fileId = file.file_data_triage_id || (fileDataTriageResponse.indexOf(file) + 1);
          const fileName = file.file_name || "Unknown";

          // Get the current mappings and formulas for this file from state
          const fileMappings = mappings[fileId] || {};
          const fileFormulas = formulas[fileId] || {};

          // Create a row for each column (same columns as displayed in UI)
          columns.forEach(column => {
            const columnId = column.id;
            
            // Get current mappings from state (what user has edited)
            const currentMappedHeaders = fileMappings[columnId] || [];
            const currentFormula = fileFormulas[columnId] || "";
            
            // Compute sample output (same as displayed in UI)
            // Pass the file object so we can get first_row data directly
            const sampleOutput = computeSampleOutput(fileId, columnId, fileName, file);

            const row = {
              "File Name": fileName,
              "Standard Header": column.name,
              "Mapped Headers": Array.isArray(currentMappedHeaders) 
                ? currentMappedHeaders.join(", ") 
                : currentMappedHeaders || "",
              "Computation Formula": currentFormula,
              "Sample Output": sampleOutput,
              "Is Mandatory": column.isMandatory ? "Yes" : "No",
              "Status": file.data_triage_status || "",
            };

            sheetData.push(row);
          });
        });

        if (sheetData.length > 0) {
          sheets.push({
            name: `${fileTypeCode} - ${period}`,
            data: sheetData,
          });
        }
      });
    });

    if (sheets.length === 0) {
      console.warn("No data to export - no uploaded files found matching criteria");
      return;
    }

    console.log("Exporting to Excel - sheets:", sheets);
    console.log("Exporting to Excel - sheets count:", sheets.length);

    try {
      console.log("Calling createExcelFile...");
      const blob = await createExcelFile(sheets);
      console.log("Excel blob created:", blob, "Type:", typeof blob, "Size:", blob?.size);
      
      if (blob) {
        const fileName = `Review_Mappings_${axonId || "Export"}_${new Date().toISOString().split("T")[0]}.xlsx`;
        console.log("Triggering download for:", fileName);
        
        // Use navigator.msSaveBlob for IE/Edge compatibility
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob, fileName);
          console.log("Download triggered via msSaveOrOpenBlob");
          return;
        }

        // Create blob URL
        const blobUrl = window.URL.createObjectURL(blob);
        console.log("Blob URL created:", blobUrl);
        
        // Create a hidden link outside React's control
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        link.style.display = "none";
        
        // Append to body outside of any React portal/modal
        document.body.appendChild(link);
        
        // Dispatch a real mouse click event (not React synthetic)
        const clickEvent = new MouseEvent("click", {
          view: window,
          bubbles: true,
          cancelable: false
        });
        link.dispatchEvent(clickEvent);
        console.log("Native click event dispatched");
        
        // Cleanup after a longer delay
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
          console.log("Download cleanup complete");
        }, 3000);
      } else {
        console.error("createExcelFile returned null or undefined blob");
      }
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      console.error("Error stack:", error?.stack);
    }
  }, [fileDataTriageResponse, uploadedFileIdentifiers, getPeriodCode, mappings, formulas, parseCsvHeaders, axonId, getFirstRowDataForFile]);

  // Reset mappings and formulas to the original API response values
  // Priority: computation_formula (user-saved mappings) > ai_mapped_headers (AI-generated mappings)
  const handleReset = useCallback(() => {
    if (!fileDataTriageResponse || fileDataTriageResponse.length === 0) {
      console.warn("No fileDataTriageResponse to reset from");
      return;
    }

    const initialMappings = {};
    const initialFormulas = {};

    // Helper: normalize a string for loose matching (trim + lowercase + collapse spaces/underscores)
    const norm = (s) =>
      (s || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, " ");

    fileDataTriageResponse.forEach((file, index) => {
      const fileId = file.file_data_triage_id || index + 1;

      // Build a lookup of this file's standard headers (client_headers CSV)
      const fileClientHeaders = parseCsvHeaders(file.client_headers);
      const clientHeaderLookup = {};
      fileClientHeaders.forEach(h => {
        clientHeaderLookup[norm(h)] = h;
      });

      // Check if computation_formula exists (user-saved mappings from save/confirm)
      // If it does, use it; otherwise fall back to ai_mapped_headers
      let sourceArray = null;

      // Try to parse computation_formula first (priority)
      if (file.computation_formula) {
        let computationFormula = file.computation_formula;
        if (typeof computationFormula === 'string') {
          try {
            computationFormula = JSON.parse(computationFormula);
          } catch (e) {
            computationFormula = null;
          }
        }
        if (Array.isArray(computationFormula) && computationFormula.length > 0) {
          sourceArray = computationFormula;
        }
      }

      // Fall back to ai_mapped_headers if computation_formula is not available
      if (!sourceArray) {
        let mappedArray = file.ai_mapped_headers;
        if (typeof mappedArray === 'string') {
          try {
            mappedArray = JSON.parse(mappedArray);
          } catch (e) {
            mappedArray = null;
          }
        }
        if (Array.isArray(mappedArray) && mappedArray.length > 0) {
          sourceArray = mappedArray;
        }
      }

      if (!sourceArray || sourceArray.length === 0) return;

      sourceArray.forEach(item => {
        if (!item || !item.standard_header) return;

        // Resolve columnId to the exact value used as tableColumns[].id
        const resolvedColumnId =
          clientHeaderLookup[norm(item.standard_header)] || item.standard_header;

        // Normalize raw_header to an array of strings
        let rawHeaders = item.raw_header;
        if (typeof rawHeaders === 'string') {
          const trimmed = rawHeaders.trim();
          if (trimmed.startsWith('[')) {
            try {
              rawHeaders = JSON.parse(trimmed);
            } catch (e) {
              rawHeaders = trimmed.split(',').map(s => s.trim()).filter(Boolean);
            }
          } else {
            rawHeaders = trimmed
              ? trimmed.split(',').map(s => s.trim()).filter(Boolean)
              : [];
          }
        } else if (!Array.isArray(rawHeaders)) {
          rawHeaders = rawHeaders ? [rawHeaders] : [];
        }

        // Ensure all entries are strings
        rawHeaders = rawHeaders
          .map(h => (h == null ? "" : h.toString().trim()))
          .filter(Boolean);

        if (rawHeaders.length > 0) {
          if (!initialMappings[fileId]) initialMappings[fileId] = {};
          initialMappings[fileId][resolvedColumnId] = rawHeaders;
        }

        if (item.computation_formula && item.computation_formula.toString().trim() !== "") {
          if (!initialFormulas[fileId]) initialFormulas[fileId] = {};
          initialFormulas[fileId][resolvedColumnId] = item.computation_formula;
        }
      });
    });

    // Reset mappings and formulas to API defaults
    setMappings(initialMappings);
    setFormulas(initialFormulas);

    // Clear any selections
    setSelectedFileId(null);
    setTargetFileIds([]);
    setSelectedColumns([]);

    console.log("Mappings and formulas reset to API defaults");
  }, [fileDataTriageResponse, parseCsvHeaders]);

  // Handle file checkbox selection
  // First selected file becomes the source (expanded), subsequent selections are targets
  const handleFileSelect = useCallback((fileId) => {
    if (selectedFileId === null) {
      // No source selected, set this as source
      setSelectedFileId(fileId);
      setTargetFileIds([]);
    } else if (selectedFileId === fileId) {
      // Clicking on source, deselect everything
      setSelectedFileId(null);
      setTargetFileIds([]);
    } else {
      // Clicking on another file, toggle as target
      setTargetFileIds(prev => {
        if (prev.includes(fileId)) {
          return prev.filter(id => id !== fileId);
        } else {
          return [...prev, fileId];
        }
      });
    }
  }, [selectedFileId]);

  // Check if a file is selected as source (expanded)
  const isFileSelected = useCallback((fileId) => selectedFileId === fileId, [selectedFileId]);

  // Check if a file is selected as target
  const isTargetFile = useCallback((fileId) => targetFileIds.includes(fileId), [targetFileIds]);

  // Check if file checkbox should be checked (source or target)
  const isFileChecked = useCallback((fileId) => {
    return selectedFileId === fileId || targetFileIds.includes(fileId);
  }, [selectedFileId, targetFileIds]);

  // Determine if formula row should be shown for a file
  const shouldShowFormulaRow = useCallback((fileId) => {
    return showComputation || isFileSelected(fileId);
  }, [showComputation, isFileSelected]);

  // Determine if sample output row should be shown for a file
  const shouldShowSampleOutputRow = useCallback((fileId) => {
    return showSampleOutput || isFileSelected(fileId);
  }, [showSampleOutput, isFileSelected]);

  // Calculate rowSpan for the file name cell based on visible rows
  const getFileNameRowSpan = useCallback((fileId) => {
    let rowSpan = 1; // Main row always visible
    if (shouldShowFormulaRow(fileId)) rowSpan++;
    if (shouldShowSampleOutputRow(fileId)) rowSpan++;
    return rowSpan;
  }, [shouldShowFormulaRow, shouldShowSampleOutputRow]);

  // Get target row position class based on which sub-rows are visible
  const getTargetRowPositionClass = useCallback((fileId, rowType) => {
    if (!isTargetFile(fileId)) return '';
    
    const hasFormula = shouldShowFormulaRow(fileId);
    const hasSampleOutput = shouldShowSampleOutputRow(fileId);
    
    // Main row is always 'first'
    if (rowType === 'main') {
      // If no sub-rows, it's both first and last (only row)
      if (!hasFormula && !hasSampleOutput) {
        return 'target-row target-row-only';
      }
      return 'target-row target-row-first';
    }
    
    // Formula row
    if (rowType === 'formula') {
      // If no sample output, formula is the last row
      if (!hasSampleOutput) {
        return 'target-row target-row-last';
      }
      // Otherwise it's middle
      return 'target-row target-row-middle';
    }
    
    // Sample output row is always last when visible
    if (rowType === 'sampleOutput') {
      return 'target-row target-row-last';
    }
    
    return '';
  }, [isTargetFile, shouldShowFormulaRow, shouldShowSampleOutputRow]);

  // Handle dropdown click - also selects the file as source
  const handleDropdownClick = useCallback((fileId) => {
    if (!isFileSelected(fileId)) {
      setSelectedFileId(fileId);
      setTargetFileIds([]);
    }
  }, [isFileSelected]);

  // Handle column header checkbox selection
  const handleColumnSelect = useCallback((columnId) => {
    setSelectedColumns(prev => {
      if (prev.includes(columnId)) {
        return prev.filter(id => id !== columnId);
      } else {
        return [...prev, columnId];
      }
    });
  }, []);

  // Check if a column is selected
  const isColumnSelected = useCallback((columnId) => selectedColumns.includes(columnId), [selectedColumns]);

  // Apply mappings and formulas from source file to selected target files for selected columns
  const handleApplyToMultipleFiles = useCallback(() => {
    if (!selectedFileId || selectedColumns.length === 0 || targetFileIds.length === 0) {
      return;
    }

    const sourceFileId = selectedFileId;
    let exceptionsCount = 0;

    // Copy mappings for selected columns to target files only
    setMappings(prev => {
      const newMappings = { ...prev };
      selectedColumns.forEach(columnId => {
        const sourceMapping = prev[sourceFileId]?.[columnId] || [];
        targetFileIds.forEach(targetFileId => {
          const targetClientHeaders = getClientHeadersForFile(targetFileId);
          
          // Filter source mappings to only include headers that exist in target file
          const validMappings = sourceMapping.filter(header => 
            targetClientHeaders.includes(header)
          );
          
          // Count exceptions (headers that don't exist in target)
          const invalidCount = sourceMapping.length - validMappings.length;
          if (invalidCount > 0) {
            exceptionsCount += invalidCount;
          }
          
          newMappings[targetFileId] = {
            ...newMappings[targetFileId],
            [columnId]: [...validMappings],
          };
        });
      });
      return newMappings;
    });

    // Copy formulas for selected columns to target files only
    setFormulas(prev => {
      const newFormulas = { ...prev };
      selectedColumns.forEach(columnId => {
        const sourceFormula = prev[sourceFileId]?.[columnId] || "";
        targetFileIds.forEach(targetFileId => {
          newFormulas[targetFileId] = {
            ...newFormulas[targetFileId],
            [columnId]: sourceFormula,
          };
        });
      });
      return newFormulas;
    });

    // Show toast message if there were exceptions
    if (exceptionsCount > 0) {
      setApplyToastMessage({
        message: `Changes have been partially applied to selected files. ${exceptionsCount} Exception${exceptionsCount > 1 ? 's' : ''} found`,
        type: 'warning'
      });
      // Auto-hide toast after 5 seconds
      setTimeout(() => setApplyToastMessage(null), 5000);
    }

    // Clear target selection after applying
    setTargetFileIds([]);
  }, [selectedFileId, selectedColumns, targetFileIds, getClientHeadersForFile]);

  // Handles refresh button click - fetches latest data while preserving existing user edits
  const handleRefresh = useCallback(async () => {
    if (isRefreshing || !axonId) return;

    setIsRefreshing(true);
    try {
      const response = await AIService.getFileDataTriage(axonId);
      console.log("Refresh - File Data Triage API Response:", response);

      let newData = [];
      if (Array.isArray(response)) {
        newData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        newData = response.data;
      }

      if (newData.length === 0) {
        setIsRefreshing(false);
        return;
      }

      // Merge new data with existing data - preserve user edits for existing files
      // Get IDs of existing files
      const existingFileIds = new Set(
        fileDataTriageResponse.map(f => f.file_data_triage_id)
      );

      // Find new files that don't exist in current response
      const newFiles = newData.filter(
        f => !existingFileIds.has(f.file_data_triage_id)
      );

      // Update existing files' metadata (status, etc.) without touching mappings/formulas
      const updatedExistingFiles = fileDataTriageResponse.map(existingFile => {
        const updatedFile = newData.find(
          f => f.file_data_triage_id === existingFile.file_data_triage_id
        );
        if (updatedFile) {
          // Merge: keep the new API data for file metadata but mappings/formulas are in separate state
          return updatedFile;
        }
        return existingFile;
      });

      // Combine updated existing files with new files
      const mergedData = [...updatedExistingFiles, ...newFiles];
      setFileDataTriageResponse(mergedData);

      // Pre-populate mappings and formulas ONLY for newly added files
      // Priority: computation_formula (user-saved) > ai_mapped_headers (AI-generated)
      if (newFiles.length > 0) {
        const norm = (s) =>
          (s || "")
            .toString()
            .trim()
            .toLowerCase()
            .replace(/[\s_]+/g, " ");

        const newMappings = {};
        const newFormulas = {};

        newFiles.forEach((file, index) => {
          const fileId = file.file_data_triage_id || (mergedData.indexOf(file) + 1);

          const fileClientHeaders = parseCsvHeaders(file.client_headers);
          const clientHeaderLookup = {};
          fileClientHeaders.forEach(h => {
            clientHeaderLookup[norm(h)] = h;
          });

          // Check if computation_formula exists (user-saved mappings from save/confirm)
          // If it does, use it; otherwise fall back to ai_mapped_headers
          let sourceArray = null;

          // Try to parse computation_formula first (priority)
          if (file.computation_formula) {
            let computationFormula = file.computation_formula;
            if (typeof computationFormula === 'string') {
              try { computationFormula = JSON.parse(computationFormula); } catch (e) { computationFormula = null; }
            }
            if (Array.isArray(computationFormula) && computationFormula.length > 0) {
              sourceArray = computationFormula;
            }
          }

          // Fall back to ai_mapped_headers if computation_formula is not available
          if (!sourceArray) {
            let mappedArray = file.ai_mapped_headers;
            if (typeof mappedArray === 'string') {
              try { mappedArray = JSON.parse(mappedArray); } catch (e) { mappedArray = null; }
            }
            if (Array.isArray(mappedArray) && mappedArray.length > 0) {
              sourceArray = mappedArray;
            }
          }

          if (!sourceArray || sourceArray.length === 0) return;

          sourceArray.forEach(item => {
            if (!item || !item.standard_header) return;

            const resolvedColumnId =
              clientHeaderLookup[norm(item.standard_header)] || item.standard_header;

            let rawHeaders = item.raw_header;
            if (typeof rawHeaders === 'string') {
              const trimmed = rawHeaders.trim();
              if (trimmed.startsWith('[')) {
                try { rawHeaders = JSON.parse(trimmed); } catch (e) {
                  rawHeaders = trimmed.split(',').map(s => s.trim()).filter(Boolean);
                }
              } else {
                rawHeaders = trimmed ? trimmed.split(',').map(s => s.trim()).filter(Boolean) : [];
              }
            } else if (!Array.isArray(rawHeaders)) {
              rawHeaders = rawHeaders ? [rawHeaders] : [];
            }

            rawHeaders = rawHeaders
              .map(h => (h == null ? "" : h.toString().trim()))
              .filter(Boolean);

            if (rawHeaders.length > 0) {
              if (!newMappings[fileId]) newMappings[fileId] = {};
              newMappings[fileId][resolvedColumnId] = rawHeaders;
            }

            if (item.computation_formula && item.computation_formula.toString().trim() !== "") {
              if (!newFormulas[fileId]) newFormulas[fileId] = {};
              newFormulas[fileId][resolvedColumnId] = item.computation_formula;
            }
          });
        });

        // Merge new mappings/formulas with existing ones (preserving existing)
        if (Object.keys(newMappings).length > 0) {
          setMappings(prev => ({ ...prev, ...newMappings }));
        }
        if (Object.keys(newFormulas).length > 0) {
          setFormulas(prev => ({ ...prev, ...newFormulas }));
        }

        console.log("Refresh - Added new files:", newFiles.map(f => f.file_name));
        console.log("Refresh - New mappings:", newMappings);
        console.log("Refresh - New formulas:", newFormulas);
      }
    } catch (error) {
      console.error("Error refreshing file data triage:", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [axonId, isRefreshing, fileDataTriageResponse, parseCsvHeaders]);

  // Handle Save button click - save draft data to API
  const handleSave = useCallback(async () => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      // Build payload for ALL files across all file types (same structure as confirm mapping)
      // Filter to only include actionable files
      // IMPORTANT: Use map first to preserve original index, then filter
      const payload = fileDataTriageResponse
        .map((file, originalIndex) => ({
          file,
          fileId: file.file_data_triage_id || originalIndex + 1,
        }))
        .filter(({ file }) => isActionableStatus(file?.data_triage_status))
        .map(({ file, fileId }) => {
          const fileMappings = mappings[fileId] || {};
          const fileFormulas = formulas[fileId] || {};

          // Get columns for THIS specific file from its client_headers
          const fileClientHeaders = parseCsvHeaders(file.client_headers);

          // Build header_json array using this file's own columns
          const headerJsonArray = fileClientHeaders.map(columnName => {
            const rawHeaders = fileMappings[columnName] || [];
            const computationFormula = fileFormulas[columnName] || "";
            
            return {
              standard_header: columnName,
              raw_header: rawHeaders,
              computation_formula: computationFormula,
            };
          });

          return {
            file_data_triage_id: fileId,
            client_headers: file.client_headers || "",
            computation_formula: JSON.stringify(headerJsonArray),
            last_modified_by: userEmail,
          };
        });

      console.log("Saving draft data:", payload);
      const response = await AIService.saveDraftData(payload);
      console.log("Save draft response:", response);
      
      // Optionally show success message or close modal
    } catch (error) {
      console.error("Failed to save draft data:", error);
      // Optionally show error message to user
    } finally {
      setIsSaving(false);
    }
  }, [fileDataTriageResponse, mappings, formulas, parseCsvHeaders, userEmail, isSaving]);

  // Check if all mandatory fields are filled for EVERY file across all fileTypes and periods.
  // A mandatory column is considered filled if it has at least one raw header selected in the dropdown
  // OR has a computation formula entered - one of them is sufficient.
  //
  // IMPORTANT: Only validate files that came from OrderFileUpload page (excludes Replaced/Deleted).
  // fileDataTriageResponse can have extra files that we should ignore for validation.
  // The mappings state keys use column IDs from each file's own client_headers.
  //
  // ADDITIONAL CHECK: For each period (CY/PY), there must be at least one JE file AND one TB file.
  const canConfirmMapping = useMemo(() => {
    if (!fileDataTriageResponse || fileDataTriageResponse.length === 0) return false;

    const norm = (s) =>
      (s || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, " ");

    // Helper to check if file is from OrderFileUpload page (excludes Replaced/Deleted)
    const isUploadedFile = (file) => {
      if (uploadedFileIdentifiers.size === 0) return true; // Backwards compatibility
      // Use filename + period combination for matching
      const fileName = (file.file_name || "").trim();
      const filePeriod = getPeriodCode(file.period);
      const identifier = `${fileName}|${filePeriod}`;
      return uploadedFileIdentifiers.has(identifier);
    };

    // Check if there's at least one actionable file from uploaded files
    const hasActionableUploadedFiles = fileDataTriageResponse.some(
      file => isUploadedFile(file) && isActionableStatus(file?.data_triage_status)
    );
    if (!hasActionableUploadedFiles) return false;

    // Presence checks count all uploaded files, including files whose triage is complete.
    const uploadedFiles = fileDataTriageResponse.filter((file) =>
      isUploadedFile(file),
    );

    // Keep actionable files separate for mandatory mapping validation.
    const uploadedActionableFiles = fileDataTriageResponse.filter(
      file => isUploadedFile(file) && isActionableStatus(file?.data_triage_status)
    );

    // Group files by period and file type
    const filesByPeriodAndType = {
      CY: { JE: false, TB: false },
      PY: { JE: false, TB: false },
    };

    uploadedFiles.forEach(file => {
      const filePeriod = getPeriodCode(file.period);
      const fileType = (file.file_type || file.fileType || "").toString().trim().toUpperCase();
      
      if (filePeriod === "CY" || filePeriod === "PY") {
        if (fileType === "JE") {
          filesByPeriodAndType[filePeriod].JE = true;
        } else if (fileType === "TB") {
          filesByPeriodAndType[filePeriod].TB = true;
        }
      }
    });

    // Check if each period has at least one JE AND one TB file
    // Only validate periods that have at least one file
    const hasCYFiles = uploadedFiles.some(f => getPeriodCode(f.period) === "CY");
    const hasPYFiles = uploadedFiles.some(f => getPeriodCode(f.period) === "PY");
    // If CY has files, it must have both JE and TB
    if (hasCYFiles && (!filesByPeriodAndType.CY.JE || !filesByPeriodAndType.CY.TB)) {
      return false;
    }

    // If PY has files, it must have both JE and TB
    if (hasPYFiles && (!filesByPeriodAndType.PY.JE || !filesByPeriodAndType.PY.TB)) {
      return false;
    }

    // Validate only files that came from OrderFileUpload page and have actionable status
    const validationResults = fileDataTriageResponse
      .map((file, index) => {
        const fileId = file.file_data_triage_id || index + 1;

        // Skip files not from OrderFileUpload page
        if (!isUploadedFile(file)) {
          return { file: file.file_name, skipped: true, valid: true, reason: 'not uploaded' };
        }

        // Skip non-actionable files
        if (!isActionableStatus(file?.data_triage_status)) {
          return { file: file.file_name, skipped: true, valid: true };
        }

        // Parse ai_mapped_headers
        let mappedArray = file.ai_mapped_headers;
        if (typeof mappedArray === 'string') {
          try { mappedArray = JSON.parse(mappedArray); } catch (e) { mappedArray = null; }
        }
        
        if (!Array.isArray(mappedArray) || mappedArray.length === 0) {
          return { file: file.file_name, fileId, noMappedHeaders: true, valid: true };
        }

        // Build a lookup from normalized standard_header to actual client_header column ID
        const fileClientHeaders = parseCsvHeaders(file.client_headers);
        const clientHeaderLookup = {};
        fileClientHeaders.forEach(h => {
          clientHeaderLookup[norm(h)] = h;
        });

        // Find all mandatory columns and their resolved IDs
        const mandatoryColumns = mappedArray
          .filter(item => item && item.standard_header && (item.is_mandatory === true || item.is_mandatory === "true"))
          .map(item => ({
            standardHeader: item.standard_header,
            resolvedId: clientHeaderLookup[norm(item.standard_header)] || item.standard_header,
          }));

        if (mandatoryColumns.length === 0) {
          return { file: file.file_name, fileId, noMandatory: true, valid: true };
        }

        const fileMappings = mappings[fileId] || {};
        const fileFormulas = formulas[fileId] || {};

        // Check each mandatory column
        const columnResults = mandatoryColumns.map(({ standardHeader, resolvedId }) => {
          const rawHeaders = fileMappings[resolvedId] || [];
          const computationFormula = (fileFormulas[resolvedId] || "").trim();
          const isFilled = rawHeaders.length > 0 || computationFormula.length > 0;
          const hasFormulaError =
            computationFormula.length > 0 && Boolean(getFormulaError(fileId, resolvedId));
          
          return {
            standardHeader,
            resolvedId,
            rawHeaders,
            hasFormula: computationFormula.length > 0,
            isFilled,
            hasFormulaError,
          };
        });

        const allFilled = columnResults.every(c => c.isFilled && !c.hasFormulaError);
        const missingColumns = columnResults.filter(c => !c.isFilled);

        return {
          file: file.file_name,
          fileId,
          fileType: file.file_type,
          period: file.period,
          mandatoryCount: mandatoryColumns.length,
          filledCount: columnResults.filter(c => c.isFilled).length,
          valid: allFilled,
          missingColumns: missingColumns.map(c => c.resolvedId),
        };
      });

    return validationResults.every(r => r.valid);
  }, [fileDataTriageResponse, uploadedFileIdentifiers, getPeriodCode, mappings, formulas, parseCsvHeaders, isActionableStatus, getFormulaError]);

  // Get detailed info about files with missing mandatory mappings (for UI display)
  // Shows files with missing mappings from uploaded files only (excludes Replaced/Deleted)
  const missingMappingsInfo = useMemo(() => {
    if (!fileDataTriageResponse || fileDataTriageResponse.length === 0) return [];

    const norm = (s) =>
      (s || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, " ");

    // Helper to check if file is from OrderFileUpload page (excludes Replaced/Deleted)
    const isUploadedFile = (file) => {
      if (uploadedFileIdentifiers.size === 0) return true; // Backwards compatibility
      // Use filename + period combination for matching
      const fileName = (file.file_name || "").trim();
      const filePeriod = getPeriodCode(file.period);
      const identifier = `${fileName}|${filePeriod}`;
      return uploadedFileIdentifiers.has(identifier);
    };

    const missingFiles = [];

    fileDataTriageResponse.forEach((file, index) => {
      const fileId = file.file_data_triage_id || index + 1;

      // Skip files not from OrderFileUpload page
      if (!isUploadedFile(file)) return;

      // Skip non-actionable files
      if (!isActionableStatus(file?.data_triage_status)) return;

      const fileName = file.file_name || `File ${fileId}`;
      const fileType = file.file_type || "Unknown";
      const period = file.period || "";
      const sheetName = file.sheet_name || file.sheetName || file.sheet || "";

      // Parse ai_mapped_headers
      let mappedArray = file.ai_mapped_headers;
      if (typeof mappedArray === 'string') {
        try { mappedArray = JSON.parse(mappedArray); } catch (e) { mappedArray = null; }
      }
      if (!Array.isArray(mappedArray) || mappedArray.length === 0) return;

      // Build lookup
      const fileClientHeaders = parseCsvHeaders(file.client_headers);
      const clientHeaderLookup = {};
      fileClientHeaders.forEach(h => {
        clientHeaderLookup[norm(h)] = h;
      });

      // Find missing mandatory columns
      const missingColumns = [];
      mappedArray.forEach(item => {
        if (!item || !item.standard_header) return;
        if (item.is_mandatory === true || item.is_mandatory === "true") {
          const resolvedColumnId = clientHeaderLookup[norm(item.standard_header)] || item.standard_header;
          
          const fileMappings = mappings[fileId] || {};
          const fileFormulas = formulas[fileId] || {};
          const rawHeaders = fileMappings[resolvedColumnId] || [];
          const computationFormula = (fileFormulas[resolvedColumnId] || "").trim();
          
          if (rawHeaders.length === 0 && computationFormula.length === 0) {
            missingColumns.push(resolvedColumnId);
          }
        }
      });

      if (missingColumns.length > 0) {
        missingFiles.push({
          fileName,
          fileId,
          fileType,
          period,
          sheetName,
          missingColumns,
        });
      }
    });

    return missingFiles;
  }, [fileDataTriageResponse, uploadedFileIdentifiers, getPeriodCode, mappings, formulas, parseCsvHeaders, isActionableStatus]);

  // Get info about periods missing JE or TB files (for UI display)
  const missingFileTypesInfo = useMemo(() => {
    if (!fileDataTriageResponse || fileDataTriageResponse.length === 0) return [];

    // Helper to check if file is from OrderFileUpload page (excludes Replaced/Deleted)
    const isUploadedFile = (file) => {
      if (uploadedFileIdentifiers.size === 0) return true;
      const fileName = (file.file_name || "").trim();
      const filePeriod = getPeriodCode(file.period);
      const identifier = `${fileName}|${filePeriod}`;
      return uploadedFileIdentifiers.has(identifier);
    };

    // Check JE/TB presence across every uploaded file, regardless of triage status.
    const uploadedFiles = fileDataTriageResponse.filter(isUploadedFile);

    // Group files by period and file type
    const filesByPeriodAndType = {
      CY: { JE: false, TB: false },
      PY: { JE: false, TB: false },
    };

    uploadedFiles.forEach(file => {
      const filePeriod = getPeriodCode(file.period);
      const fileType = (file.file_type || file.fileType || "").toString().trim().toUpperCase();
      
      if (filePeriod === "CY" || filePeriod === "PY") {
        if (fileType === "JE") {
          filesByPeriodAndType[filePeriod].JE = true;
        } else if (fileType === "TB") {
          filesByPeriodAndType[filePeriod].TB = true;
        }
      }
    });

    const missingInfo = [];

    // Check CY period
    const hasCYFiles = uploadedFiles.some(f => getPeriodCode(f.period) === "CY");
    if (hasCYFiles) {
      const missingTypes = [];
      if (!filesByPeriodAndType.CY.JE) missingTypes.push("JE");
      if (!filesByPeriodAndType.CY.TB) missingTypes.push("TB");
      if (missingTypes.length > 0) {
        missingInfo.push({
          period: "Current Year (CY)",
          missingTypes,
        });
      }
    }

    // Check PY period
    const hasPYFiles = uploadedFiles.some(f => getPeriodCode(f.period) === "PY");
    if (hasPYFiles) {
      const missingTypes = [];
      if (!filesByPeriodAndType.PY.JE) missingTypes.push("JE");
      if (!filesByPeriodAndType.PY.TB) missingTypes.push("TB");
      if (missingTypes.length > 0) {
        missingInfo.push({
          period: "Previous Year (PY)",
          missingTypes,
        });
      }
    }

    return missingInfo;
  }, [fileDataTriageResponse, uploadedFileIdentifiers, getPeriodCode]);

  // Handle Confirm Mapping button click
  const handleConfirmMapping = useCallback(async () => {
    if (isConfirming || !canConfirmMapping) return;

    setIsConfirming(true);
    try {
      // Build payload for ALL files across all file types (not just current selection)
      // Filter to only include actionable files
      // IMPORTANT: Use map first to preserve original index, then filter
      const payload = fileDataTriageResponse
        .map((file, originalIndex) => ({
          file,
          fileId: file.file_data_triage_id || originalIndex + 1,
        }))
        .filter(({ file }) => isActionableStatus(file?.data_triage_status))
        .map(({ file, fileId }) => {
          const fileMappings = mappings[fileId] || {};
          const fileFormulas = formulas[fileId] || {};

          // Get columns for THIS specific file from its client_headers
          const fileClientHeaders = parseCsvHeaders(file.client_headers);

          // Build header_json array using this file's own columns
          const headerJsonArray = fileClientHeaders.map(columnName => {
            const rawHeaders = fileMappings[columnName] || [];
            const computationFormula = fileFormulas[columnName] || "";
            
            return {
              standard_header: columnName,
              raw_header: rawHeaders,
              computation_formula: computationFormula,
            };
          });

          return {
            file_data_triage_id: fileId,
            client_headers: file.client_headers || "",
            computation_formula: JSON.stringify(headerJsonArray),
            last_modified_by: userEmail,
            status: "Confirmed Mapping"
          };
        });

      const response = await AIService.confirmMappings(payload);

      const hasPyTbFile = fileDataTriageResponse.some((file) => {
        const type = (file.file_type || file.fileType || "").toString().trim().toUpperCase();
        if (type !== "TB" || getPeriodCode(file.period) !== "PY") return false;
        const identifier = `${(file.file_name || "").trim()}|PY`;
        return uploadedFileIdentifiers.size === 0 || uploadedFileIdentifiers.has(identifier);
      });
      if (orderId && hasPyTbFile) {
        try {
          await FilesService.savePyCyFlag(orderId, {
            considerPYEndingBalanceForCY: usePyClosingBalance,
            modifiedBy: userEmail,
          });
        } catch (flagError) {
          console.error("Failed to save PY/CY flag:", flagError);
        }
      }
      
      // Log confirm mapping clicked activity
      try {
        await FilesService.logConfirmMappingClicked(axonId, userEmail);
        console.log("Confirm mapping activity logged successfully");
      } catch (logError) {
        console.error("Failed to log confirm mapping activity:", logError);
        // Don't block the success flow if logging fails
      }
      
      // Close modal on success
      if (onConfirm) {
        onConfirm();
      }
    } catch (error) {
      console.error("Failed to confirm mappings:", error);
    } finally {
      setIsConfirming(false);
    }
  }, [fileDataTriageResponse, mappings, formulas, parseCsvHeaders, userEmail, isConfirming, canConfirmMapping, onConfirm, orderId, usePyClosingBalance, getPeriodCode, uploadedFileIdentifiers]);

  return (
    <main className="review-mappings-modal review-mappings-page">
      <header className="review-mappings-page-header">
        <div className="review-mappings-header-left">
          <button
            type="button"
            className="review-mappings-back-btn"
            onClick={onClose}
            aria-label="Back"
          >
            <ArrowLeftIcon width={14} height={14} color="#A4A3B1" />
          </button>
          <h1>Review Mapping</h1>
          <div className="review-mappings-year-tabs">
            <button
              className={`year-tab ${activeTab === "CY" ? "active" : ""}`}
              onClick={() => setActiveTab("CY")}
            >
              CY
            </button>
            <button
              className={`year-tab ${activeTab === "PY" ? "active" : ""}`}
              onClick={() => setActiveTab("PY")}
            >
              PY
            </button>
          </div>
        </div>
        <div className="review-mappings-disclaimer">
          <span>
            {CHAT_ACTIONS.DISCLAIMER1}
            <CustomTooltipBottom
              trigger={
                <InfoCircleIcon
                  width="14.33"
                  height="14.33"
                  color="#85B9FD"
                />
              }
              normalText={CHAT_ACTIONS.DISCLAIMER_TOOLTIP}
            />
          </span>
        </div>
        <div className="review-mappings-header-right">
          <button
            className={`review-mappings-refresh-btn ${isRefreshing ? 'refreshing' : ''}`}
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshIcon width={16} height={16} color="#A4A3B1" className={isRefreshing ? 'spinning' : ''}/>
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <div className="review-mappings-ai-summary">
            <div className="ai-summary-icon">
              <AILogo width={20} height={20} color="#FFE600" />
            </div>
            <div className="ai-summary-text">
              <span className="ai-summary-title">AI Summary</span>
              <span
                className="ai-summary-count"
                style={{ color: aiAnomalies.length === 0 ? '#34CE63' : undefined }}
              >
                {aiAnomalies.length} Anomalies
              </span>
            </div>
            <button
              className="ai-summary-details-btn"
              onClick={() => aiAnomalies.length > 0 && setShowAIAnomaliesModal(true)}
            >
              Details
            </button>
          </div>
        </div>
      </header>
      <div className="review-mappings-page-body">
        {/* Toast message for apply to multiple files exceptions */}
        {applyToastMessage && (
          <div className="apply-toast-message warning">
            <WarningTriangle width={20} height={20} color="#FFE600" />
            <span>{applyToastMessage.message}</span>
            <button 
              className="toast-close-btn"
              onClick={() => setApplyToastMessage(null)}
            >
              ×
            </button>
          </div>
        )}

        {showPyClosingBalanceOption && (
          <div className="py-closing-balance-option">
            <input
              type="checkbox"
              id="use-py-closing-balance"
              checked={usePyClosingBalance}
              onChange={(e) => handlePyClosingBalanceChange(e.target.checked)}
            />
            <label htmlFor="use-py-closing-balance">
              Use PY Closing Balance as CY Beginning Balance
            </label>
          </div>
        )}
        
        {/* File type and display options */}
        <div className="review-mappings-top-section">
          <div className="review-mappings-left-controls">
            {/* File Type Dropdown */}
            <div className="review-mappings-file-type">
              <label className="review-mappings-label">File Type</label>
              <MotifFormField>
                <MotifSelect
                  value={fileType}
                  onChange={(e) => setFileType(e)}
                  filter
                >
                  {fileTypeOptions.map((option) => (
                    <MotifOption key={option} value={option} title={option}>
                      {option}
                    </MotifOption>
                  ))}
                </MotifSelect>
              </MotifFormField>
            </div>

            {/* Checkboxes */}
            <div className="review-mappings-checkboxes">
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="show-computation"
                  checked={showComputation}
                  onChange={(e) => setShowComputation(e.target.checked)}
                />
                <label htmlFor="show-computation">Show Computation</label>
              </div>
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="show-sample-output"
                  checked={showSampleOutput}
                  onChange={(e) => setShowSampleOutput(e.target.checked)}
                />
                <label htmlFor="show-sample-output">Show Sample Output</label>
              </div>
            </div>
          </div>
        </div>

        {/* Mappings Table */}
        <div className="review-mappings-table-section">
          {/* Loading overlay for initial load */}
          {isInitialLoading && (
            <div className="review-mappings-table-loading-overlay initial-loading">
              <div className="loading-spinner"></div>
              <span>Loading mappings data...</span>
            </div>
          )}
          {/* Loading overlay for refresh */}
          {isRefreshing && !isInitialLoading && (
            <div className="review-mappings-table-loading-overlay">
              <div className="loading-spinner"></div>
              <span>Refreshing data...</span>
            </div>
          )}
          {/* Single table: File Name and Client Field columns are sticky so rows always stay aligned */}
          <div className="review-mappings-table-wrapper">
              <table ref={mappingsTableRef} className="review-mappings-table">
                <thead>
                  <tr>
                    <th colSpan={2} className="combined-header sticky-col sticky-col-1 sticky-col-edge">File Name / Standard Headers</th>
                    {tableColumns.map((column) => {
                      const columnHasAnomaly = hasAnyAgentSummaryForColumn(column.id);
                      return (
                      <th key={column.id} className={columnHasAnomaly ? 'mandatory-warning-header' : ''}>
                        <div className="header-content">
                          <input 
                            type="checkbox" 
                            className="header-checkbox"
                            checked={isColumnSelected(column.id)}
                            onChange={() => handleColumnSelect(column.id)}
                          />
                          {column.name}
                          {isMandatory(column) && (
                            <span className="required-asterisk">*</span>
                          )}
                          {columnHasAnomaly && (
                            <WarningTriangle width={16} height={16} color="#D07200" />
                          )}
                        </div>
                      </th>
                      );
                    })}
                    <th className="end-border-cell"></th>
                  </tr>
                </thead>
                <tbody>
                  {filesData.map((file) => (
                    <React.Fragment key={file.id}>
                      {/* Main Row */}
                      <tr className={`file-row ${isFileSelected(file.id) ? 'selected-row selected-row-first' : ''} ${getTargetRowPositionClass(file.id, 'main')}`}>
                        {/* File Name Cell with Checkbox - spans multiple rows based on visible sub-rows */}
                        <td className="file-name-td sticky-col sticky-col-1" rowSpan={getFileNameRowSpan(file.id)}>
                          <div className="file-name-cell">
                            <input
                              type="checkbox"
                              className="file-checkbox"
                              checked={isFileChecked(file.id)}
                              onChange={() => handleFileSelect(file.id)}
                            />
                            <div className="file-icon">JE</div>
                            <div className="file-info">
                              <span className="file-name">{file.fileName}</span>
                              {file.sheetName && (
                                <span className="file-sheet-label">
                                  {file.sheetName}
                                  <button
                                    type="button"
                                    className="sheet-preview-btn"
                                    aria-label={`Preview ${file.sheetName}`}
                                    onClick={() => setPreviewPopup({ previewKey: file.previewKey, fileName: file.fileName, sheetName: file.sheetName })}
                                  >
                                    <EyeIcon width={14} height={14} color="#4696ff" />
                                  </button>
                                </span>
                              )}
                              {isFileSelected(file.id) && filesData.length > 1 && (
                                <span className="source-file-label">Source File (Copy from)</span>
                              )}
                            </div>
                          </div>
                        </td>
                        {/* Client Field Cell */}
                        <td className="client-field-td sticky-col sticky-col-2 sticky-col-edge">
                          <div className="client-field-cell">
                            <div className="client-field-details">
                              <div className="client-field-heading">
                                <span className="client-field-text">Client Field</span>
                                <InfoIcon width={14} height={14} color="#A4A3B1" />
                              </div>
                              {file.status && (
                                <span className="client-field-status">
                                  {file.status === "AIHeadersMapped" && (
                                    <AILogo
                                      width={12}
                                      height={12}
                                      color="#FFE600"
                                      className="ai-logo-icon"
                                    />
                                  )}
                                  {formatDataTriageStatus(file.status)}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        {tableColumns.map((column) => {
                          const cellHasAnomaly = hasAgentSummary(file.id, column.id);
                          return (
                          <td key={column.id} className={cellHasAnomaly ? 'mandatory-warning-cell' : ''}>
                            <div className="table-dropdown-cell" onMouseDown={(e) => { e.stopPropagation(); handleDropdownClick(file.id); }}>
                              <MotifFormField>
                                <MotifSelect 
                                  className="table-select"
                                  value={getMapping(file.id, column.id)}
                                  onChange={(value) => handleMappingChange(file.id, column.id, value)}
                                  multiple
                                  placeholder="---"
                                  visibleOptions={4}
                                  filter
                                  closeMenuOnSelect={false}
                                  
                                >
                                  {getClientHeadersForFile(file.id).map((header, headerIndex) => (
                                    <MotifOption key={`${file.id}-${column.id}-${headerIndex}-${header}`} value={header} title={header}>
                                      {header}
                                    </MotifOption>
                                  ))}
                                </MotifSelect>
                              </MotifFormField>
                            </div>
                          </td>
                          );
                        })}
                        <td className="end-border-cell"></td>
                      </tr>
                      {/* Formula Row - shown when showComputation is checked OR file is selected */}
                      {shouldShowFormulaRow(file.id) && (
                        <tr className={`sub-row formula-row ${isFileSelected(file.id) ? 'selected-row selected-row-middle' : ''} ${getTargetRowPositionClass(file.id, 'formula')}`}>
                          <td className="client-field-td sub-row-td sticky-col sticky-col-2 sticky-col-edge">
                            <span className="sub-row-label">Formula</span>
                          </td>
                          {tableColumns.map((column) => {
                            const inputKey = `${file.id}-${column.id}`;
                            const isActive = activeFormulaInput?.fileId === file.id && activeFormulaInput?.columnId === column.id;
                            return (
                              <td key={column.id} className="sub-row-td formula-cell">
                                <div className="formula-input-wrapper">
                                  <input
                                    ref={(el) => formulaInputRefs.current[inputKey] = el}
                                    type="text"
                                    className={`formula-input ${getFormulaError(file.id, column.id) ? 'formula-input-error' : ''}`}
                                    value={getFormula(file.id, column.id)}
                                    onChange={(e) => handleFormulaChange(file.id, column.id, e.target.value)}
                                    disabled={isFormulaLocked(file.id, column.id)}
                                    placeholder="---"
                                    title={getFormula(file.id, column.id) || getFormulaError(file.id, column.id) || ''}
                                  />
                                  {getFormulaError(file.id, column.id) && (
                                    <div className="formula-error-tooltip">
                                      {getFormulaError(file.id, column.id)}
                                    </div>
                                  )}
                                  {getFormula(file.id, column.id) && !isFormulaLocked(file.id, column.id) && (
                                    <button 
                                      className="formula-close-btn"
                                      onClick={() => clearFormula(file.id, column.id)}
                                    >
                                      <CloseIcon width={12} height={12} color="#A4A3B1" />
                                    </button>
                                  )}
                                  {/* Suggestions Dropdown - rendered in a portal with fixed positioning
                                      so it is not clipped by the horizontally-scrolling table container */}
                                  {isActive && formulaSuggestions.length > 0 && formulaInputRefs.current[inputKey] &&
                                    createPortal(
                                      (() => {
                                        const rect = formulaInputRefs.current[inputKey].getBoundingClientRect();
                                        return (
                                          <div
                                            className="formula-suggestions-dropdown formula-suggestions-dropdown--portal"
                                            style={{
                                              position: 'fixed',
                                              top: rect.bottom + 4,
                                              left: rect.left,
                                              width: rect.width,
                                              zIndex: 10000,
                                            }}
                                          >
                                            <div className="suggestions-header">Suggestions</div>
                                            {formulaSuggestions.map((suggestion, idx) => (
                                              <div
                                                key={idx}
                                                className={`suggestion-item ${suggestion.type}`}
                                                onMouseDown={(e) => {
                                                  // prevent input blur before click handler fires
                                                  e.preventDefault();
                                                  handleSuggestionSelect(file.id, column.id, suggestion);
                                                }}
                                              >
                                                <input
                                                  type="checkbox"
                                                  className="suggestion-checkbox"
                                                  readOnly
                                                  checked={false}
                                                />
                                                <span className="suggestion-text">{suggestion.display}</span>
                                              </div>
                                            ))}
                                          </div>
                                        );
                                      })(),
                                      document.body
                                    )
                                  }
                                </div>
                              </td>
                            );
                          })}
                          <td className="end-border-cell"></td>
                        </tr>
                      )}
                      {/* Sample Output Row - shown when showSampleOutput is checked OR file is selected */}
                      {shouldShowSampleOutputRow(file.id) && (
                        <tr className={`sub-row sample-output-row ${isFileSelected(file.id) ? 'selected-row selected-row-last' : ''} ${getTargetRowPositionClass(file.id, 'sampleOutput')}`}>
                          <td className="client-field-td sub-row-td sticky-col sticky-col-2 sticky-col-edge">
                            <span className="sub-row-label">Sample Output</span>
                          </td>
                          {tableColumns.map((column) => (
                            <td key={column.id} className="sub-row-td sample-output-cell">
                              <span className="sample-output-text">
                                {getSampleOutputForMapping(file.id, column.id)}
                              </span>
                            </td>
                          ))}
                          <td className="end-border-cell"></td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
          </div>
        </div>

        {/* Missing File Types Warning (JE/TB per period) */}
        {missingFileTypesInfo.length > 0 && (
          <div className="missing-mappings-warning">
            <span className="warning-icon">⚠</span>
            <div className="warning-content">
              <span className="warning-title">Missing required file types:</span>
              <ul className="missing-files-list">
                {missingFileTypesInfo.map(({ period, missingTypes }) => (
                  <li key={period}>
                    <strong>{period}</strong>: Please add at least one {missingTypes.join(" and one ")} file
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Missing Mandatory Mappings Warning */}
        {missingMappingsInfo.length > 0 && (
          <div className="missing-mappings-warning">
            <span className="warning-icon">⚠</span>
            <div className="warning-content">
              <span className="warning-title">Missing mandatory mappings:</span>
              <ul className="missing-files-list">
                {missingMappingsInfo.map(({ fileName, fileId, fileType, period, sheetName, missingColumns }) => (
                  <li key={`${fileId}-${fileName}`}>
                    <strong>{fileName}</strong>{sheetName ? ` - ${sheetName}` : ''} ({fileType}{period ? ` - ${period}` : ''}): {missingColumns.join(", ")}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
      {previewPopup && createPortal(
        <div className="sheet-preview-overlay" onClick={() => setPreviewPopup(null)}>
          <div className="sheet-preview-popup" role="dialog" aria-label="Sheet preview" onClick={(event) => event.stopPropagation()}>
            <div className="sheet-preview-header">
              <span className="sheet-preview-title">{previewPopup.fileName} - {previewPopup.sheetName}</span>
              <button type="button" className="sheet-preview-close" aria-label="Close preview" onClick={() => setPreviewPopup(null)}>
                <CloseIcon width={16} height={16} color="#DEDEE2" />
              </button>
            </div>
            <div className="sheet-preview-body">
              {sheetPreviews[previewPopup.previewKey]?.status === "error" ? (
                <div className="sheet-preview-message">Unable to load the preview. Please try again later.</div>
              ) : !previewPopupRows ? (
                <div className="sheet-preview-message">Loading preview...</div>
              ) : previewPopupRows.length === 0 ? (
                <div className="sheet-preview-message">No preview data available for this sheet.</div>
              ) : (
                (() => {
                  const hasRowLabel = previewPopupRows.every((row) => /^row\s*\d+$/i.test(String(row?.[0] ?? "")));
                  const offset = hasRowLabel ? 1 : 0;
                  const columnCount = Math.max(...previewPopupRows.map((row) => row.length)) - offset;
                  return (
                    <table className="sheet-preview-table">
                      <thead>
                        <tr>
                          <th className="sheet-preview-index"></th>
                          {Array.from({ length: columnCount }, (_, index) => (
                            <th key={index}>{getColumnLabel(index)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewPopupRows.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            <td className="sheet-preview-index">{rowIndex + 1}</td>
                            {Array.from({ length: columnCount }, (_, columnIndex) => (
                              <td key={columnIndex}>{String(row[columnIndex + offset] ?? "")}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
      <footer className="review-mappings-page-footer">
        <div className="review-mappings-footer">
          <div className="footer-left-buttons">
            <button 
              className="export-excel-btn"
              onClick={handleExportToExcel}
              disabled={!fileDataTriageResponse || fileDataTriageResponse.length === 0}
            >
              <ExcelIcon width={18} height={18} color="#DEDEE2" />
              Export to Excel
            </button>
            <button 
              className="reset-btn"
              onClick={handleReset}
              disabled={!fileDataTriageResponse || fileDataTriageResponse.length === 0}
            >
              <RefreshIcon width={16} height={16} color="#A4a3b1"/>
              Reset
            </button>
          </div>
          <div className="footer-right-buttons">
            <button 
              className="save-btn" 
              onClick={handleSave}
              disabled={isSaving || filesData.length === 0}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
            <button 
              className="apply-multiple-btn" 
              disabled={!selectedFileId || selectedColumns.length === 0 || targetFileIds.length === 0}
              onClick={handleApplyToMultipleFiles}
            >
              Apply to multiple files
            </button>
            <button 
              className="incomplete-data-btn"
              onClick={() => {
                if (onIncompleteData) {
                  onIncompleteData("Update status to Data Received Incomplete");
                }
                onClose();
              }}
            >
              Incomplete Data
            </button>
            <button
              className="confirm-mapping-btn"
              onClick={handleConfirmMapping}
              disabled={!canConfirmMapping || isConfirming}
            >
              {isConfirming ? "Confirming..." : "Confirm Mapping"}
            </button>
          </div>
        </div>
      </footer>

      {/* AI Anomalies Modal */}
      <AIAnomaliesModal
        show={showAIAnomaliesModal}
        onClose={() => setShowAIAnomaliesModal(false)}
        onGoToField={(anomaly) => {
          // Close modal and scroll/highlight the relevant field
          setShowAIAnomaliesModal(false);
          // TODO: Implement navigation to specific field
          console.log("Navigate to field:", anomaly);
        }}
        anomalies={aiAnomalies}
      />
    </main>
  );
};

// Route wrapper: navigates back to the Files tab on close/confirm
export const ReviewMappingsRoute = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { orderId } = useParams();
  const outletContext = useOutletContext();

  const goBack = (refreshFiles = false) =>
    navigate(`/orders/${orderId}`, {
      state: { returnToFiles: true, refreshFiles },
    });

  return (
    <ReviewMappingsModal
      onClose={() => goBack(false)}
      onConfirm={() => goBack(true)}
      onIncompleteData={outletContext?.onIncompleteData}
      uploadedFiles={location.state?.uploadedFiles || []}
    />
  );
};
